import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  CalendarPlus,
  MessageCircle,
  MapPin,
  Check,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";

import { SilhouetteVehicle } from "@/components/wyvio-ui/signature/SilhouetteVehicle";

/**
 * ClientMockup — Espace client WYVIO (mobile 390×844).
 * Mission : "Je suis client. En moins de 3 secondes, je comprends où en est
 * ma voiture et je suis rassuré."
 *
 * Contraintes :
 *   • Hero immersif du véhicule (photo ou silhouette WYVIO)
 *   • Timeline simplifiée en 5 étapes compréhensibles par un particulier
 *   • Un seul CTA contextuel
 *   • Langage rassurant, jamais technique
 *   • Même ADN visuel que l'espace agence (typo, palette, ombres, radius)
 *
 * L'espace client est aussi une vitrine : le client doit se dire
 * "Le garage qui utilise WYVIO est vraiment organisé."
 */

const CLIENT = {
  firstName: "Marc",
  vehicle: "Porsche 911 GT3",
  variant: "Coupé · Craie",
  silhouette: "coupe",
  image: null,
  agency: "Deluxe Motors Paris 8e",
  advisor: "Stéphane",
};

// Étapes côté client (5 grandes phases plutôt que 13 techniques)
const CLIENT_STEPS = [
  { key: 1, label: "Commande confirmée", done: true },
  { key: 2, label: "Votre véhicule est en préparation", done: true },
  { key: 3, label: "Contrôle final réussi", done: true },
  { key: 4, label: "Rendez-vous programmé", active: true },
  { key: 5, label: "Prise en main", done: false },
];

export default function ClientMockup() {
  return (
    <>
      <Helmet>
        <title>{`Mon suivi — ${CLIENT.vehicle} · wyvio`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      {/* Fond page = même palette agence, pour cohérence */}
      <div className="wyvio-signature-bg font-neue-haas min-h-screen">
        {/* Ruban mockup + wrapper "device" pour l'aperçu bureau */}
        <div className="mx-auto flex min-h-screen w-full max-w-[1200px] flex-col items-center justify-start px-6 pb-24 pt-14">
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-10 inline-flex items-center gap-2 rounded-full border border-[#E8E8E2] bg-white/70 px-3 py-1.5 text-[10.5px] font-medium uppercase tracking-[0.22em] text-[#6B6B67] backdrop-blur"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#151515]" />
            Mockup · Espace client mobile
          </motion.div>

          {/* Deux devices côte à côte : Accueil + Détail */}
          <div className="grid grid-cols-2 gap-14">
            <MobileFrame testid="mobile-home">
              <ClientView />
            </MobileFrame>
            <MobileFrame testid="mobile-context">
              <ClientContextView />
            </MobileFrame>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * MobileFrame — cadre iPhone-like pour aperçu bureau du mockup mobile.
 * En production, cet emballage disparaît : la vue occupe tout l'écran.
 */
function MobileFrame({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="relative mx-auto h-[844px] w-[390px] overflow-hidden rounded-[46px] bg-[#F8F8F5]"
      style={{
        boxShadow:
          "0 0 0 12px #151515, 0 0 0 13px rgba(21,21,21,0.6), 0 40px 80px -30px rgba(21,21,21,0.35)",
      }}
    >
      {/* Notch */}
      <div
        aria-hidden
        className="absolute left-1/2 top-2 z-30 h-[26px] w-[110px] -translate-x-1/2 rounded-full bg-[#151515]"
      />
      {/* Status bar spacer */}
      <div className="relative h-full overflow-hidden">{children}</div>
    </div>
  );
}

/**
 * ClientView — l'écran principal client.
 * Répond immédiatement à "Où en est ma voiture ?"
 */
function ClientView() {
  return (
    <div className="flex h-full flex-col overflow-y-auto">
      {/* Padding pour la notch */}
      <div className="h-[42px]" />

      {/* Header client — logo discret + prénom */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex items-center justify-between px-6"
      >
        <div className="flex items-center gap-1.5">
          <span className="text-[15px] font-medium tracking-[-0.02em] text-[#151515]">
            wyvio
          </span>
          <span
            aria-hidden
            className="inline-block h-1 w-1 rounded-full bg-[#151515]"
          />
        </div>
        <div
          className="flex h-8 w-8 items-center justify-center rounded-full bg-[#151515] text-[11px] font-medium text-white"
          aria-hidden
        >
          M
        </div>
      </motion.div>

      {/* Hero véhicule */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="mx-6 mt-6 overflow-hidden rounded-[24px] bg-[#151515]"
      >
        <div className="relative aspect-[4/3] w-full">
          {CLIENT.image ? (
            <img
              src={CLIENT.image}
              alt={CLIENT.vehicle}
              className="h-full w-full object-cover"
            />
          ) : (
            <SilhouetteVehicle
              type={CLIENT.silhouette}
              variant="dark"
              className="h-full w-full"
            />
          )}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(21,21,21,0.05) 0%, rgba(21,21,21,0) 40%, rgba(21,21,21,0.55) 100%)",
            }}
          />
          <div className="absolute inset-x-0 bottom-0 p-6 text-white">
            <p className="text-[10.5px] font-medium uppercase tracking-[0.22em] text-white/55">
              Votre véhicule
            </p>
            <h1 className="mt-2 text-[26px] font-medium leading-[1] tracking-[-0.02em]">
              {CLIENT.vehicle}
            </h1>
            <p className="mt-1.5 text-[12.5px] font-normal text-white/60">
              {CLIENT.variant}
            </p>
          </div>
        </div>
      </motion.section>

      {/* Message rassurant principal — la vraie réponse à "où en est ma voiture" */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="mx-6 mt-6"
      >
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full bg-[#C8FF2F]"
          />
          <p className="text-[10.5px] font-medium uppercase tracking-[0.22em] text-[#8A8A80]">
            Bonne nouvelle
          </p>
        </div>
        <p className="mt-4 text-[24px] font-medium leading-[1.15] tracking-[-0.02em] text-[#151515]">
          Votre 911 GT3 est prête.<br />
          Rendez-vous <span className="wyvio-tabular">mardi 11 mars à 10h30</span>.
        </p>
      </motion.section>

      {/* Timeline 5 étapes simplifiée */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.35 }}
        className="mx-6 mt-8"
      >
        <ClientTimeline steps={CLIENT_STEPS} />
      </motion.section>

      {/* CTA unique contextuel */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
        className="mx-6 mt-8"
      >
        <button
          data-testid="client-cta-primary"
          className="group flex w-full items-center justify-center gap-2 rounded-[16px] bg-[#151515] py-4 text-[14px] font-medium tracking-[-0.005em] text-white transition-colors hover:bg-[#242424]"
        >
          <CalendarPlus className="h-4 w-4" strokeWidth={2} />
          Ajouter à mon calendrier
          <span className="wyvio-btn-arrow ml-1" aria-hidden>
            →
          </span>
        </button>
        <button
          data-testid="client-cta-secondary"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-[16px] border border-[#E8E8E2] bg-white py-3.5 text-[13px] font-medium text-[#151515] transition-colors hover:bg-[#F2F2EE]"
        >
          <MessageCircle className="h-4 w-4" strokeWidth={2} />
          Contacter Stéphane
        </button>
      </motion.section>

      {/* Signature agence discrète */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7, duration: 0.5 }}
        className="mx-6 mb-8 mt-10 flex items-center justify-between border-t border-[#E8E8E2] pt-6"
      >
        <div>
          <p className="text-[10.5px] font-medium uppercase tracking-[0.2em] text-[#8A8A80]">
            Suivi assuré par
          </p>
          <p className="mt-1.5 text-[13.5px] font-medium tracking-[-0.005em] text-[#151515]">
            {CLIENT.agency}
          </p>
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F2F2EE] text-[11px] font-medium text-[#151515]">
          DM
        </div>
      </motion.section>
    </div>
  );
}

/**
 * ClientTimeline — timeline verticale simplifiée pour le client.
 * 5 étapes seulement, langage rassurant.
 */
function ClientTimeline({ steps }) {
  return (
    <div className="relative">
      <div
        aria-hidden
        className="absolute left-[15px] top-4 bottom-4 w-px"
        style={{ background: "#EEEEE8" }}
      />
      <ul className="space-y-3.5">
        {steps.map((s, i) => {
          const active = s.active;
          const done = s.done && !active;
          return (
            <motion.li
              key={s.key}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: 0.06 * i + 0.4,
                duration: 0.4,
              }}
              className="relative flex items-center gap-4"
            >
              <div
                className={`relative z-10 flex flex-shrink-0 items-center justify-center rounded-full ${
                  active ? "wyvio-segment-active h-8 w-8" : "h-8 w-8"
                }`}
                style={
                  active
                    ? {
                        background: "#C8FF2F",
                        boxShadow:
                          "0 0 0 4px #F8F8F5, 0 0 0 5px rgba(200,255,47,0.35)",
                      }
                    : done
                      ? {
                          background: "transparent",
                          color: "#8A8A80",
                          boxShadow: "inset 0 0 0 1px #E8E8E2",
                        }
                      : {
                          background: "transparent",
                          color: "#D9D9D2",
                          boxShadow: "inset 0 0 0 1px #EEEEE8",
                        }
                }
              >
                {done && (
                  <Check className="h-3.5 w-3.5 text-[#8A8A80]" strokeWidth={2} />
                )}
                {active && (
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ background: "#151515" }}
                  />
                )}
              </div>
              <p
                className={`${
                  active
                    ? "text-[15px] font-medium text-[#151515]"
                    : done
                      ? "text-[13.5px] font-normal text-[#6B6B67]"
                      : "text-[13.5px] font-normal text-[#C4C4B8]"
                } tracking-[-0.005em]`}
              >
                {s.label}
              </p>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * ClientContextView — variante contextuelle où le client peut agir.
 * Utilisée pour montrer l'écran quand une action lui est demandée
 * (ex: choisir un créneau). On y voit l'invitation rassurante + 3 créneaux.
 */
function ClientContextView() {
  const SLOTS = [
    { day: "Lundi", date: "10 mars", time: "17h00" },
    { day: "Mardi", date: "11 mars", time: "10h30", recommended: true },
    { day: "Mercredi", date: "12 mars", time: "14h00" },
  ];

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="h-[42px]" />

      {/* Header */}
      <div className="flex items-center justify-between px-6">
        <div className="flex items-center gap-1.5">
          <span className="text-[15px] font-medium tracking-[-0.02em] text-[#151515]">
            wyvio
          </span>
          <span
            aria-hidden
            className="inline-block h-1 w-1 rounded-full bg-[#151515]"
          />
        </div>
        <span
          className="inline-flex items-center gap-1 rounded-full bg-[#F2F2EE] px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.16em] text-[#6B6B67]"
          aria-hidden
        >
          Étape 4/5
        </span>
      </div>

      {/* Hero mini + question */}
      <div className="mx-6 mt-6">
        <div className="mb-6 overflow-hidden rounded-[20px] bg-[#151515]">
          <div className="relative aspect-[16/9] w-full">
            <SilhouetteVehicle
              type={CLIENT.silhouette}
              variant="dark"
              className="h-full w-full"
            />
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(21,21,21,0) 40%, rgba(21,21,21,0.65) 100%)",
              }}
            />
            <div className="absolute inset-x-0 bottom-0 p-4">
              <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-white/55">
                Votre {CLIENT.vehicle}
              </p>
            </div>
          </div>
        </div>

        <p className="flex items-center gap-2 text-[10.5px] font-medium uppercase tracking-[0.22em] text-[#8A8A80]">
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full bg-[#C8FF2F]"
          />
          Une petite chose
        </p>
        <h1 className="mt-3 text-[26px] font-medium leading-[1.15] tracking-[-0.02em] text-[#151515]">
          Choisissez le moment qui vous convient pour la remise des clés.
        </h1>
        <p className="mt-3 text-[13.5px] font-normal leading-[1.55] text-[#4A4A46]">
          Votre véhicule vous attend chez {CLIENT.agency}. Sélectionnez le
          créneau qui vous arrange le mieux.
        </p>
      </div>

      {/* 3 créneaux */}
      <div className="mx-6 mt-6 space-y-3">
        {SLOTS.map((s, i) => (
          <motion.button
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * i + 0.35, duration: 0.4 }}
            className={`group flex w-full items-center justify-between rounded-[18px] p-5 text-left transition-all ${
              s.recommended
                ? "bg-white shadow-wyvio-elev"
                : "bg-white shadow-wyvio-elev opacity-90 hover:opacity-100"
            }`}
          >
            <div>
              <p className="text-[15px] font-medium tracking-[-0.005em] text-[#151515]">
                {s.day} <span className="text-[#8A8A80]">·</span> {s.date}
              </p>
              <p className="mt-1 wyvio-tabular text-[13px] font-normal text-[#6B6B67]">
                {s.time}
              </p>
            </div>
            {s.recommended ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#C8FF2F] px-2.5 py-1 text-[9.5px] font-medium uppercase tracking-[0.18em] text-[#151515]">
                <Sparkles className="h-2.5 w-2.5" strokeWidth={2} />
                Recommandé
              </span>
            ) : (
              <ArrowUpRight
                className="h-4 w-4 text-[#C4C4B8] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#151515]"
                strokeWidth={2}
              />
            )}
          </motion.button>
        ))}
      </div>

      {/* Petit lien secondaire */}
      <div className="mx-6 mt-6 flex items-center justify-between border-t border-[#E8E8E2] pt-5">
        <div className="flex items-center gap-2 text-[12px] font-normal text-[#6B6B67]">
          <MapPin className="h-3.5 w-3.5" strokeWidth={1.8} />
          16 avenue George V, Paris 8e
        </div>
        <button className="text-[12px] font-medium text-[#151515]">
          Voir sur la carte
        </button>
      </div>

      <div className="flex-1" />

      {/* Bandeau de confiance très discret */}
      <div className="mx-6 mb-6 mt-6 flex items-center justify-center gap-2 text-[11px] font-normal text-[#8A8A80]">
        <span
          aria-hidden
          className="inline-block h-1 w-1 rounded-full bg-[#C4C4B8]"
        />
        Suivi par {CLIENT.agency}
      </div>
    </div>
  );
}
